<script lang="ts">
    import { Recaptcha, recaptcha } from "svelte-recaptcha-v2";
    import { slide } from "svelte/transition";
    import { afterUpdate, SvelteComponent } from 'svelte';
    import { getStoresContext } from "~/stores";
    import type { StoresKey } from "~/stores";
    import type { CustomComponent } from "../custom-component";
    import { BROWSER } from "esm-env";

    export let request_store_key: StoresKey;
    export let fragment: Fragment;
    export let csrf_token: string;
    export let submit_button: CustomComponent<SvelteComponent> | undefined = undefined;
    export let submit_button_class: string = '';
    

    let storeSpecificForm: boolean = false;
    const stores_context = getStoresContext(request_store_key);
    const { stores, site, T, store } = stores_context;

    $: t = $T;
    $: storeSpecificForm = !!$store;
    $: RECAPTCHA_SITE_KEY = $site.recaptcha_site_key;
    const privacyStatementlink = stores_context.fieldUtils().getLink('link', fragment) || '';
    const State = {                      
        idle: 'idle',
        validation_failed: 'validation_failed',
        recaptcha_request: 'recaptcha_request',
        recaptcha_success: 'recaptcha_success',
        feedback_success: 'feedback_success'
    };

    function onSubmit(event: Event) {
        event.preventDefault();
        if (!form_valid) return (state = State.validation_failed); 
        state = State.recaptcha_request;
        doRecaptcha();
    }

    function encodeForm() {
        const may_drop = [storeId && { storeId }, requiresResponse && { requiresResponse: 'true' }]
            .filter(Boolean)
            .reduce((a, b) => Object.assign(a, b), {});
        return new URLSearchParams({
            feedback,
            name,
            emailAddress,
            types: types.join(','),
            source,
            ...may_drop
        }).toString();
    }

    const onCaptchaSuccess = async (event:any) => {
        const token = event.detail.token;

        state = State.recaptcha_success;
        const res = await fetch(`/api/feedback.json?token=${token}`, {
            method: 'POST',
            body: encodeForm(),
            headers: {
                'Content-Type': 'text/plain',
                'X-CSRF-Protection': csrf_token,
            }
        });
        if (res.ok) {
            return (state = State.feedback_success);
        }
        console.error(await res.json());
    };

    async function doRecaptcha() {
        recaptcha.execute();
    }

    // Enums
    const [GENERAL, STORE] = ['GENERAL', 'STORE'];
    const target_options = [GENERAL, STORE];
    const type_options = ['THANKS', 'SUGGESTION', 'COMPLAINT', 'OTHER'];

    $: target_selected = storeSpecificForm? STORE: GENERAL;
    let state = State.idle;


    //State
    $: is_idle = [State.idle, State.validation_failed].includes(state);
    $: is_validation_failed = state == State.validation_failed;
    $: is_success = state == State.feedback_success;

    afterUpdate(() => {
       // Get back to idle after user has fixed input
        if (is_validation_failed && form_valid) {
        state = State.idle;
        }
    });

    // Validation
    $: need_to_select_store = target_selected == STORE;
    $: email_invalid = !/^[a-zA-Z]+[a-zA-Z0-9.+_-]+@[a-zA-Z0-9._-]+\.[a-zA-Z.]{2,5}$/.test(
    emailAddress
    );
    $: is_required_fields = [feedback, name, emailAddress].every(Boolean);
    $: store_if_target = need_to_select_store ? !!storeId : true;
    $: form_valid = is_required_fields && !email_invalid && store_if_target;

    // Data
    let feedback = '';
    let name = '';
    let emailAddress = '';
    $: storeId = storeSpecificForm ? $store?.id : '';
    let types:any = [];
    let requiresResponse = false;
    let source = 'WEB';
</script>


    <form class="feedback" id="feedback" on:submit|preventDefault={onSubmit} transition:slide|local name="feedbackForm" novalidate>
        {#if !RECAPTCHA_SITE_KEY}
            <span class="invalid-feedback d-block">Missing Recaptcha site key.</span>
        {/if}
        <fieldset disabled={!RECAPTCHA_SITE_KEY}>
            <p class="required mb-3">{t`feedback.required_fields_marked_by_star`}</p>
            {#if !storeSpecificForm}
                <div class="mb-3">
                    <label class="form-label font-weight-normal required" for="type">{t`feedback.target`}</label>
                    {#each target_options as target}
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                id="target-{target}"
                                type="radio"
                                name="target"
                                bind:group={target_selected}
                                value={target}
                                checked={target == target_selected}
                            />
                            <label class="form-check-label font-weight-normal" for="target-{target}">
                                {t`feedback.target.${target.toLowerCase()}`}
                            </label>
                        </div>
                    {/each}
                </div>
            {/if}
            {#if target_selected == STORE &&  !storeSpecificForm}
                <div class="col-10 col-md-6 mb-3">
                    <select class="form-select" name="storeid" bind:value={storeId}>
                        <option value={''} selected>{t`feedback.choose_shop`}</option>
                        {#if $stores.length}
                            {#each $stores as store}
                                <option value={store.id}>{store.name}</option>
                            {/each}
                        {/if}
                    </select>
                    {#if is_validation_failed && !storeId}
                        <span class="invalid-feedback d-block">{t`feedback.error.please_choose_a_store`}</span>
                    {/if}
                </div>
            {/if}
            <div class="mb-3">
                <label class="form-label font-weight-normal required" for="type">{t`feedback.type_of_feedback`}</label>
                <div class="options-feedback form-checks">
                    {#each type_options as type}
                        <label class="form-check-label me-3 font-weight-normal">
                            <input
                                class="form-check-input"
                                id="target-{type}"
                                type="checkbox"
                                value={type}
                                bind:group={types}
                            />
                            {t`feedback.type.${type.toLowerCase()}`}
                        </label>
                    {/each}
                </div>
            </div>
            <div class="mb-3">
                <label class="required form-label font-weight-normal" for="feedback">{t`feedback.message`}</label>
                <textarea class="form-control" rows="4" name="feedback" required bind:value={feedback} />
                {#if is_validation_failed && !feedback}
                    <span class="invalid-feedback d-block">{t`feedback.error.field_required`}</span>
                {/if}
            </div>
            <div class="row">
                <div class="col-sm-6">
                    <div class="mb-3">
                        <label class="required form-label font-weight-normal" for="name">{t`feedback.name`}</label>
                        <input type="text" class="form-control" name="name" required bind:value={name} />
                        {#if is_validation_failed && !name}
                            <span class="invalid-feedback d-block">{t`feedback.error.field_required`}</span>
                        {/if}
                    </div>
                </div>
                <div class="col-sm-6">
                    <div class="mb-3">
                        <label class="required form-label font-weight-normal" for="emailAddress">{t`feedback.email`}</label>
                        <input
                            type="email"
                            class="form-control"
                            name="emailAddress"
                            required
                            bind:value={emailAddress}
                        />
                        {#if is_validation_failed && email_invalid && emailAddress}
                            <span class="invalid-feedback d-block">{t`feedback.error.invalid_email_address`}</span>
                        {/if}
                        {#if is_validation_failed && !emailAddress}
                            <span class="invalid-feedback d-block">{t`feedback.error.field_required`}</span>
                        {/if}
                    </div>
                </div>
            </div>
            <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="requiresResponse"
                    bind:checked={requiresResponse}
                />
                <label class="font-weight-normal form-check-label" for="requiresResponse">{t`feedback.rsvp`}</label>
            </div>
            <div class="mb-3">
                <a class="registry-link font-weight-normal privacy-link" target="_blank" href={privacyStatementlink.href}>
                    {t`feedback.privacy_statement`}
                </a>
            </div>
            {#if !is_success}
                {#if submit_button}
                    <svelte:component 
                        this={submit_button?.component} 
                        {...submit_button?.props}
                        on:click={onSubmit}
                        disabled={!is_idle}>
                        {t`feedback.send`}
                    </svelte:component>
                {:else}
                    <button class={submit_button_class} 
                        on:click={onSubmit}
                        disabled={!is_idle}>
                        {t`feedback.send`}
                    </button>
                {/if}
            {/if}
            {#if BROWSER && RECAPTCHA_SITE_KEY}
                <Recaptcha
                    sitekey={RECAPTCHA_SITE_KEY}
                    badge={"top"}
                    size={"invisible"}
                    on:success={onCaptchaSuccess}
                    on:error={() => console.error('recaptcha error')}    
                />
            {/if}
            {#if is_validation_failed}
                <div class="mb-3 text-danger pt-2">
                    <p>{t`feedback.error.check_all_required_fields`}</p>
                </div>
            {/if}
        </fieldset>
    </form>


{#if is_success}
    <p>{t`feedback.thank_you`}</p>
{/if}

<style lang="scss">

    .feedback {
        select {
            border: solid 2px var(--color-text);
            border-radius: 30px;
            background: var(--color-white);
            padding: 0.375rem 0.75rem;
            cursor: pointer;
        }
        .options-feedback {
            display:flex; 
            flex-direction: column; 
            margin: 5px 0; 
            text-transform: capitalize;
        }
        .privacy-link {
            font-size: 0.875rem;
            text-decoration: underline; 
            color:var(--brandColor1);
        }
        
    }
    .required::after {
        content: ' *';
        color: var(--brandColor3);
        opacity: 0.6;
    }
    .form-check-input:checked {
        background-color: var(--brandColor3);
        border-color: var(--brandColor3);
    }

</style>